import type { ReactNode } from 'react'
import { useState } from 'react'
import { useSearchParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import { usePostHog } from '@posthog/react'
import type { ReturnsTrackingInfoResponseLogisticsInfo } from '@itsrever/rever-api'
import { useInstructionsURL } from '@/hooks/use-instructions-url'
import { useDeliveryNoteURL } from '@/hooks/use-delivery-note-url'
import { NavArrowRightIcon } from '@/ui/icons/nav-arrow-right'
import { PaperIcon } from '@/ui/icons/paper'
import { PasteClipboardIcon } from '@/ui/icons/paste-clipboard'
import { PinIcon } from '@/ui/icons/pin'
import { ShopIcon } from '@/ui/icons/shop'
import { CopyIcon } from '@/ui/icons/copy'
import { CheckIcon } from '@/ui/icons/check'
import { BarcodeIcon } from '@/ui/icons/barcode'
import { DownloadIcon } from '@/ui/icons/download'
import { shouldPollLabel, useLabelURL } from '@/hooks/use-label-url'
import { useRequestNewLabel } from '@/hooks/use-request-new-label'
import { HomeIcon } from '@/ui/icons/home'
import { CubeIcon } from '@/ui/icons/cube'
import { CalendarRotateIcon } from '@/ui/icons/calendar-rotate'
import { LabelStatus, QrLabel, isImageLabelURL } from './label-status'
import { warehouseAddressLines } from '@/ui/components/warehouse-address'

interface ReturnLogisticsProps {
  logisticsInfo: ReturnsTrackingInfoResponseLogisticsInfo
  // Once the carrier has the parcel, the label is reference material, not a next step.
  isParcelCollected: boolean
}

export const ReturnLogistics = ({
  logisticsInfo,
  isParcelCollected
}: ReturnLogisticsProps) => {
  const { t } = useTranslation()

  const [searchParams] = useSearchParams()

  const posthog = usePostHog()

  const email = searchParams.get('email') ?? undefined
  const processId = searchParams.get('processId') ?? undefined

  const labelStatus = logisticsInfo?.label_status

  const {
    data: labelURL,
    isLoading: isLoadingLabel,
    isFetching: isFetchingLabel,
    isError: isLabelError,
    refetch: refetchLabel
  } = useLabelURL(
    { process_id: processId, email: email },
    { poll: shouldPollLabel(labelStatus, isParcelCollected) }
  )

  const labelExpired = labelStatus === 'EXPIRED'
  const newLabel = useRequestNewLabel({ process_id: processId, email: email })

  const { data: instructionsURL } = useInstructionsURL({
    process_id: processId,
    email: email
  })

  const { data: deliveryNoteURL } = useDeliveryNoteURL({
    process_id: processId,
    email: email
  })

  const trackingUrl = logisticsInfo?.tracking_url
  const trackingId = logisticsInfo?.tracking_id
  const returnMethod = logisticsInfo?.return_method
  const storesListUrl = logisticsInfo.in_store_info?.store_list_url
  const barcodeUrl = logisticsInfo.in_store_info?.barcode_url
  const dropoffPointsUrl = logisticsInfo.dropoff_info?.points_url
  const pickupAddress = logisticsInfo.pickup_info?.pickup_address
  const pickupScheduleUrl = logisticsInfo.pickup_info?.schedule_url
  const selfShippingAddress =
    logisticsInfo.self_shipping_info?.warehouse_address
  const maxDaysToSend = logisticsInfo.self_shipping_info?.max_days_to_send

  const formattedPickupAddress = pickupAddress
    ? [
        [pickupAddress.address_line_1, pickupAddress.address_line_2]
          .filter(Boolean)
          .join(' '),
        [pickupAddress.postcode, pickupAddress.city].filter(Boolean).join(' '),
        pickupAddress.country
      ]
        .filter(Boolean)
        .join(', ')
    : ''

  const formattedSelfShippingAddress = selfShippingAddress
    ? warehouseAddressLines(selfShippingAddress).join(', ')
    : ''

  const showPickupAddress =
    (returnMethod === 'DELIVERY_PICKUP' || returnMethod === 'HOME_PICK_UP') &&
    pickupAddress

  // A return without a label (in store, GLS, no return method) has nothing to
  // generate or track, though a carrier document may still exist.
  const labelRequired = labelStatus !== 'NOT_REQUIRED'

  // The printer card is the shopper's next step only before collection, and its
  // generating state only for a label the backend still reports as pending.
  const showLabelCard =
    labelRequired &&
    !isParcelCollected &&
    (labelExpired ||
      isLoadingLabel ||
      !!labelURL ||
      (labelStatus === 'PENDING' && !isLabelError))
  const labelLink = !showLabelCard && labelURL ? labelURL : null

  // A tracking number is not a label. DELIVERY_PICKUP never produces a label, yet the
  // merchant's courier still reports a tracking URL the shopper can follow, so the label
  // status alone must not hide it. IN_STORE stays hidden because the API blanks its URL:
  // the store-to-warehouse leg is not the shopper's.
  const showTracking =
    (labelRequired || !!trackingUrl) && (labelURL || trackingId)

  const hasContent =
    showLabelCard ||
    labelLink ||
    showTracking ||
    instructionsURL ||
    deliveryNoteURL ||
    storesListUrl ||
    barcodeUrl ||
    dropoffPointsUrl ||
    pickupScheduleUrl ||
    showPickupAddress ||
    formattedSelfShippingAddress

  if (!hasContent) return null

  return (
    <section className="w-full overflow-hidden rounded-lg border border-neutral-300/50 bg-white shadow-sm">
      <div className="divide-y divide-neutral-300/50">
        {showLabelCard ? (
          <LabelStatus
            labelURL={labelURL}
            isLoading={isLoadingLabel}
            isChecking={isFetchingLabel}
            onCheckAgain={() => {
              posthog?.capture('Label Check Again', { processId })
              void refetchLabel()
            }}
            processId={processId}
            expired={labelExpired}
            isRequestingNewLabel={newLabel.isPending}
            newLabelFailed={newLabel.isError}
            onRequestNewLabel={() => {
              posthog?.capture('Label Request New', { processId })
              newLabel.mutate()
            }}
          />
        ) : null}
        {showTracking ? (
          <TrackRow trackingUrl={trackingUrl} trackingId={trackingId} />
        ) : null}
        {labelLink && isImageLabelURL(labelLink) ? (
          <QrLabel labelURL={labelLink} processId={processId} />
        ) : null}
        {labelLink && !isImageLabelURL(labelLink) ? (
          <ActionRow
            href={labelLink}
            label={t('tracking_new.download_label')}
            onClick={() => posthog?.capture('Download Label', { processId })}
            icon={
              <DownloadIcon
                className="size-4 shrink-0 text-neutral-800"
                strokeWidth={1.5}
              />
            }
          />
        ) : null}
        {instructionsURL ? (
          <ActionRow
            href={instructionsURL}
            label={t('tracking_new.see_instructions')}
            onClick={() =>
              posthog?.capture('Download Instructions', { processId })
            }
            icon={
              <PaperIcon
                className="size-4 shrink-0 text-neutral-800"
                strokeWidth={1.5}
              />
            }
          />
        ) : null}
        {deliveryNoteURL ? (
          <ActionRow
            href={deliveryNoteURL}
            label={t('tracking_new.delivery_note')}
            onClick={() =>
              posthog?.capture('Download Delivery Note', { processId })
            }
            icon={
              <PasteClipboardIcon
                className="size-4 shrink-0 text-neutral-800"
                strokeWidth={1.5}
              />
            }
          />
        ) : null}
        {storesListUrl ? (
          <ActionRow
            href={storesListUrl}
            label={t('tracking_new.find_stores')}
            onClick={() => posthog?.capture('Find Stores')}
            icon={<ShopIcon className="size-4 shrink-0 text-neutral-800" />}
          />
        ) : null}
        {barcodeUrl ? (
          <ActionRow
            href={barcodeUrl}
            label={t('tracking_new.see_barcode')}
            onClick={() => posthog?.capture('Download Barcode')}
            icon={
              <BarcodeIcon
                className="size-4 shrink-0 text-neutral-800"
                strokeWidth={1.5}
              />
            }
          />
        ) : null}
        {dropoffPointsUrl ? (
          <ActionRow
            href={dropoffPointsUrl}
            label={t('tracking_new.find_dropoff')}
            onClick={() => posthog?.capture('Find DropOff Points')}
            icon={
              <PinIcon
                className="size-4 shrink-0 text-neutral-800"
                strokeWidth={1.5}
              />
            }
          />
        ) : null}
        {pickupScheduleUrl ? (
          <ActionRow
            href={pickupScheduleUrl}
            label={t('tracking_new.schedule_pickup')}
            onClick={() => posthog?.capture('Schedule New Pickup')}
            icon={
              <CalendarRotateIcon
                className="size-4 shrink-0 text-neutral-800"
                strokeWidth={1.5}
              />
            }
          />
        ) : null}
        {formattedSelfShippingAddress ? (
          <div className="group flex gap-3 px-4 py-3.5">
            <CubeIcon
              className="mt-0.5 size-4 shrink-0 text-neutral-800"
              strokeWidth={1.5}
            />
            <div className="space-y-1">
              <p className="text-sm font-medium text-neutral-900">
                {t('tracking_new.self_shipping_address')}
              </p>
              <p className="text-xs font-normal leading-relaxed text-neutral-600">
                {formattedSelfShippingAddress}
              </p>
              {maxDaysToSend ? (
                <p className="text-xs font-normal leading-relaxed text-neutral-600">
                  {t('tracking_new.self_shipping_deadline')}: {maxDaysToSend}
                </p>
              ) : null}
            </div>
          </div>
        ) : null}
        {showPickupAddress ? (
          <div className="group flex gap-3 px-4 py-3.5">
            <HomeIcon
              className="mt-0.5 size-4 shrink-0 text-neutral-800"
              strokeWidth={1.5}
            />
            <div className="space-y-1">
              <p className="text-sm font-medium text-neutral-900">
                {t('tracking_new.pickup_address')}
              </p>
              <p className="text-xs font-normal leading-relaxed text-neutral-600">
                {formattedPickupAddress}
              </p>
            </div>
          </div>
        ) : null}
      </div>
    </section>
  )
}

interface ActionRowProps {
  href: string
  label: string
  icon: ReactNode
  onClick?: () => void
}

const ActionRow = ({ href, label, icon, onClick }: ActionRowProps) => {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={onClick}
      className="group flex items-center gap-3 px-4 py-4 hover:bg-neutral-100"
    >
      {icon}
      <span className="text-sm font-medium text-neutral-900">{label}</span>
      <NavArrowRightIcon className="ml-auto size-4 text-neutral-500" />
    </a>
  )
}

const TrackRow = ({
  trackingUrl,
  trackingId
}: {
  trackingUrl: ReturnsTrackingInfoResponseLogisticsInfo['tracking_url']
  trackingId: ReturnsTrackingInfoResponseLogisticsInfo['tracking_id']
}) => {
  const { t } = useTranslation()
  const posthog = usePostHog()

  return (
    <div className="flex items-center gap-3 px-4 py-4">
      <CubeIcon className="size-4 shrink-0 text-neutral-800" />
      <span className="shrink-0 text-sm font-medium text-neutral-900">
        {t('tracking_new.track_return')}
      </span>
      <div className="ml-auto flex min-w-0 items-center gap-2 pl-2">
        {trackingId ? (
          <>
            {trackingUrl ? (
              <a
                href={trackingUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => posthog?.capture('Track Return Clicked')}
                className="min-w-0 truncate font-mono text-sm text-blue-500 underline-offset-2 hover:underline"
              >
                {trackingId}
              </a>
            ) : (
              <span className="min-w-0 truncate font-mono text-sm text-neutral-900">
                {trackingId}
              </span>
            )}
            <CopyButton value={trackingId} />
          </>
        ) : (
          <span className="rounded-md bg-neutral-100 px-2 py-0.5 text-xs text-neutral-900">
            {t('tracking_new.pending')}
          </span>
        )}
      </div>
    </div>
  )
}

const CopyButton = ({ value }: { value: string }) => {
  const { t } = useTranslation()

  const posthog = usePostHog()

  const [copied, setCopied] = useState(false)

  const handleCopy = async () => {
    posthog?.capture('Tracking ID Copied')
    await navigator.clipboard.writeText(value)
    setCopied(true)
    setTimeout(() => setCopied(false), 1500)
  }

  return (
    <button
      type="button"
      onClick={handleCopy}
      aria-label={t('tracking_new.copy_tracking')}
      className="text-neutral-500 transition-colors hover:text-neutral-700"
    >
      {copied ? (
        <CheckIcon className="size-4" />
      ) : (
        <CopyIcon className="size-4" />
      )}
    </button>
  )
}
